/**
 * Web's mark-read storage mutators.
 *
 * Web equivalents of the extension's `MARK_READ` and
 * `MARK_NOTIFICATIONS_READ` background-worker case handlers in
 * `apps/extension/src/background/index.ts`. The extension routes
 * these through its service worker because the popup/sidepanel can
 * be torn down mid-flight; the web app has a single React tree, so
 * we mutate `webStorage` inline.
 *
 * Both helpers are also responsible for keeping the global
 * `unreadCount` in sync — the headless `SidePanelNotificationList`
 * gates its "Mark all read" button on that counter, and the future
 * web badge wiring (PR 6+) reads the same field.
 *
 * Conversation read-marking is delegated to the shared
 * `conversations.markConversationRead` factory in
 * `services/conversationsStore` — same factory the SSE pipeline
 * writes through, so the in-memory msgId reverse index stays
 * consistent.
 *
 * Important: `conversationsStore.markConversationRead` runs its
 * own `webStorage.get → set` cycle OUTSIDE the `pendingWrite`
 * queue defined below, so it can race with `markAllRead` on the
 * `conversations` and `unreadCount` storage keys. Today the race
 * is benign — both operations converge on `unreadCount: 0` for
 * a given thread, so whichever write lands second produces the
 * same observable state. Future changes that diverge the two
 * write paths (e.g. partial conversation read, per-message read
 * tracking) MUST close this gap by routing the conversations-
 * factory writes through `serialise` here too, or by promoting
 * the queue into client-core so both consumers share it.
 */

import type { ReflectoStorage } from "@reflecto/client-core";
import { webStorage } from "../adapters/storage.web";

/**
 * Module-level promise chain that serialises all read-modify-write
 * cycles against `webStorage`. Without this, the IntersectionObserver
 * firing `markNotificationsRead` while the user clicks "Mark all read"
 * would race the two `webStorage.get → set` cycles — whichever `set`
 * resolves second wins, silently discarding the other's mutation. The
 * extension avoids the race because every storage write is funnelled
 * through a single background-worker message queue; the web app has
 * no such serialisation boundary, so we add one here.
 *
 * `serialise` chains each new operation onto the tail of the queue
 * and updates the tail to the result. Failures are swallowed for the
 * NEXT chain link only (so one bad write doesn't break all subsequent
 * writes); the original promise still rejects to its own caller.
 */
let pendingWrite: Promise<unknown> = Promise.resolve();
function serialise<T>(fn: () => Promise<T>): Promise<T> {
  const result = pendingWrite.then(fn);
  pendingWrite = result.catch(() => undefined);
  return result;
}

/**
 * Mark a batch of regular notifications as read by msgId. Mirrors
 * the extension's `MARK_NOTIFICATIONS_READ` handler:
 *   - load `notifications` + `unreadCount`
 *   - flip `isRead: true` on every match that wasn't already read
 *   - decrement the global counter by the number actually flipped
 *     (so a re-fire after IntersectionObserver glitches doesn't
 *     double-decrement)
 *   - skip the storage write entirely when nothing changed
 *
 * Idempotent — calling with already-read msgIds is a no-op. Serialised
 * against `markAllRead` via the module-level `pendingWrite` queue.
 */
export async function markNotificationsRead(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  return serialise(async () => {
    const { notifications = [], unreadCount = 0 } = await webStorage.get(
      "notifications",
      "unreadCount"
    );
    const idSet = new Set(ids);
    let markedCount = 0;
    const updated = notifications.map((n) => {
      if (!n.isRead && idSet.has(n.msgId)) {
        markedCount++;
        return { ...n, isRead: true };
      }
      return n;
    });
    if (markedCount === 0) return;
    const updates: Partial<ReflectoStorage> = {
      notifications: updated,
      unreadCount: Math.max(0, unreadCount - markedCount),
    };
    await webStorage.set(updates);
  });
}

/**
 * Mark every notification AND every conversation thread as read.
 * Mirrors the extension's `MARK_READ` handler:
 *   - flip `isRead: true` on every regular notification
 *   - reset `unreadCount` to 0 on every conversation thread
 *   - reset the global `unreadCount` to 0
 *
 * The reverse-index in `services/conversationsStore` is unaffected
 * (it tracks msgId → thread membership, not per-thread unread).
 * Serialised against `markNotificationsRead` via `pendingWrite`.
 */
export async function markAllRead(): Promise<void> {
  return serialise(async () => {
    const { notifications = [], conversations = {} } = await webStorage.get(
      "notifications",
      "conversations"
    );
    const markedAll = notifications.map((n) => (n.isRead ? n : { ...n, isRead: true }));
    const resetConvs = { ...conversations };
    for (const key of Object.keys(resetConvs)) {
      if (resetConvs[key].unreadCount > 0) {
        resetConvs[key] = { ...resetConvs[key], unreadCount: 0 };
      }
    }
    await webStorage.set({
      notifications: markedAll,
      unreadCount: 0,
      conversations: resetConvs,
    });
  });
}
